import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ShieldCheck, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import type { Deliverable, Plan } from '@/components/Configuracao/plansTabTypes'
import { PlansTabModalInfoTab } from '@/components/Configuracao/PlansTabModalInfoTab'
import { PlansTabModalEscopoTab } from '@/components/Configuracao/PlansTabModalEscopoTab'

export type PlansTabModalProps = {
  editingPlan: Plan | null
  isCreating: boolean
  modalTab: 'info' | 'escopo'
  saving: boolean
  deliverables: Deliverable[]
  totalHours: number
  calculatedValue: number
  onClose: () => void
  onSave: () => void
  onTabChange: (tab: 'info' | 'escopo') => void
  onPlanChange: (plan: Plan) => void
  onDeliverableChange: (idx: number, field: keyof Deliverable, value: string) => void
  onAddDeliverable: () => void
  onRemoveDeliverable: (idx: number) => void
}

export function PlansTabModal(props: PlansTabModalProps) {
  if (!props.editingPlan) return null

  return createPortal(
    <AnimatePresence>
      {props.editingPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={props.onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="relative z-10 w-full max-w-[1200px] max-h-[90vh] flex flex-col bg-[var(--bg-card)] border border-[var(--glass-border)] rounded-xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--glass-border)]">
              <h3
                className="font-semibold text-[var(--font-color-h3)]"
                style={{ fontSize: 'var(--font-size-h3)' }}
              >
                {props.isCreating
                  ? 'Novo Plano (Matrix)'
                  : `Editar Matriz do Plano: ${props.editingPlan.name}`}
              </h3>
              <button
                type="button"
                onClick={props.onClose}
                className="p-1 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--glass-bg)] transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex gap-6 px-6 border-b border-[var(--glass-border)]">
              {(['info', 'escopo'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => props.onTabChange(tab)}
                  className={cn(
                    'py-3 text-[13px] font-semibold border-b-2 transition-colors cursor-pointer',
                    props.modalTab === tab
                      ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]'
                      : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]',
                  )}
                >
                  {tab === 'info' ? 'Informações do Plano' : 'Escopo do Plano (Matriz)'}
                </button>
              ))}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              {props.modalTab === 'info' && (
                <PlansTabModalInfoTab plan={props.editingPlan} onPlanChange={props.onPlanChange} />
              )}
              {props.modalTab === 'escopo' && (
                <PlansTabModalEscopoTab
                  plan={props.editingPlan}
                  deliverables={props.deliverables}
                  totalHours={props.totalHours}
                  calculatedValue={props.calculatedValue}
                  onPlanChange={props.onPlanChange}
                  onDeliverableChange={props.onDeliverableChange}
                  onAddDeliverable={props.onAddDeliverable}
                  onRemoveDeliverable={props.onRemoveDeliverable}
                />
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--glass-border)]">
              <Button type="button" variant="ghost" onClick={props.onClose}>
                Recusar & Sair
              </Button>
              <Button
                variant="primary"
                onClick={props.onSave}
                loading={props.saving}
                icon={<ShieldCheck size={18} />}
              >
                Confirmar Matriz Oficial
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
